<script lang="ts">
    import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
    import { browser } from '$app/environment';
    import { onMount, afterUpdate, beforeUpdate } from 'svelte';
    import { trackPage, trackVariant } from '$lib/global/tracking';
    import { setGlobals, getGlobals } from '$lib/global/globals';
    import unescape from 'lodash/unescape';
    import debug from 'debug';
    import DisplayComponent from '$lib/components/DisplayComponent/DisplayComponent.svelte';
    import { SPLIT_TESTING_KEY } from '$lib/global/constants';

    // Types
    import type { ComponentType } from 'svelte';
    import type { Page } from '$lib/types/Page';

    export let pageData: Page;

    const debugLog = debug('page');
    const STkey = SPLIT_TESTING_KEY + pageData.campaignId;

    let dataToDisplay = pageData;
    let Confetti: ComponentType;

    let mounted = false;
    let prevPageId: string | null = null;
    let isHomePage = true;

    debugLog('-> initial dataToDisplay', dataToDisplay);

    beforeUpdate(() => {
        debugLog('-> beforeUpdate');
        const existingGlobals = getGlobals();
        const isPageChange = pageData.pageId !== prevPageId && mounted;

        if (browser) {
            // Check for active split testing
            if (pageData.variantPageData?.pageId) {
                const ST = window.sessionStorage;

                // Check session storage for defined variant
                let variant = ST.getItem(STkey);

                // If not present, calculate and set a variant
                if (!variant) {
                    variant = Math.random() < 0.5 ? 'A' : 'B';
                    ST.setItem(STkey, variant);
                }

                // Populate page data
                if (variant === 'A') {
                    dataToDisplay = pageData;
                } else if (variant === 'B') {
                    dataToDisplay = pageData.variantPageData;
                }
            } else {
                dataToDisplay = pageData;
            }

            // Update globals for tracking
            setGlobals({
                ...existingGlobals,
                perspectivePageSlug: dataToDisplay.pageSlug,
                perspectivePageId: dataToDisplay.pageId,
                perspectiveCompanyId: dataToDisplay.companyId,
                perspectiveCampaignId: dataToDisplay.campaignId,
                perspectiveVersionId: dataToDisplay.campaignVersionId,
                pageId: dataToDisplay.pageId,
                pageSlug: dataToDisplay.pageSlug,
                companyId: dataToDisplay.companyId,
                campaignId: dataToDisplay.campaignId,
                versionId: dataToDisplay.campaignVersionId,
                trackingTitles: getTrackingTitles(),
            });

            // only track when mounted; pageId changed and the page is not the home page
            // -> home page gets tracked in onMount
            if (isPageChange && !isHomePage) {
                trackPage({
                    pageName: dataToDisplay.pageName,
                    tag: dataToDisplay.tag,
                    campaignId: dataToDisplay.campaignId,
                    pageSlug: dataToDisplay.pageSlug,
                    isLastPage: dataToDisplay.isLastPage,
                    pageId: dataToDisplay.pageId,
                });
            }

            // Inject custom head scripts if there are any, and they're valid
            // svelte:head isn't working here, see: https://discord.com/channels/457912077277855764/1091386239124508852

            if (dataToDisplay.headScript && isPageChange) {
                const codeSnippet = unescape(dataToDisplay.headScript);

                const parser = new DOMParser();
                const parsedSnippet = parser.parseFromString(codeSnippet, 'text/html');

                const invalidElements = Array.from(parsedSnippet.body.children).filter(
                    (node) => node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE',
                );

                if (invalidElements.length > 0) {
                    console.error('Invalid HTML in code snippet.');
                } else {
                    const range = document.createRange();
                    const fragment = range.createContextualFragment(codeSnippet.trim());
                    document.head.appendChild(fragment);
                }
            }

            // update prevPageId after all updates
            if (isPageChange) {
                prevPageId = pageData.pageId;
            }
        }
    });

    onMount(() => {
        debugLog('-> onMount');

        polyfillCountryFlagEmojis();

        // Track initial home page view
        if (isHomePage) {
            trackPage({
                pageName: dataToDisplay.pageName,
                tag: dataToDisplay.tag,
                campaignId: dataToDisplay.campaignId,
                pageSlug: dataToDisplay.pageSlug,
                isLastPage: dataToDisplay.isLastPage,
                pageId: dataToDisplay.pageId,
            });

            // Track page variant if split testing is active
            trackPageVariant();
        }

        mounted = true;
    });

    afterUpdate(async () => {
        // this is needed to make sure the page data is updated and this component re-renders
        dataToDisplay = pageData;
        debugLog('-> afterUpdate dataToDisplay', dataToDisplay);

        if (dataToDisplay.animation === 'confetti' && !Confetti) {
            // CAUTION: dynamic import since it's not used on every page
            // -> this causes to trigger `afterUpdate` again!
            Confetti = (await import('$lib/components/Confetti/Confetti.svelte')).default;
        }
    });

    const trackPageVariant = () => {
        debugLog('-> trackPageVariant', dataToDisplay.variantPageData?.pageId);

        if (dataToDisplay.variantPageData?.pageId) {
            const variant = window.sessionStorage.getItem(STkey);

            if (!variant) return;

            const map = {
                A: 'Original',
                B: 'Variant',
            };

            const name = [map[variant as 'A' | 'B'], dataToDisplay.pageName].join(' - ');

            trackVariant(name);
        }
    };

    const getTrackingTitles = () => {
        let trackingTitles: Record<string, string> = {};

        if (pageData.context.trackingVersion > 'v3') {
            trackingTitles = {
                ps_first_seen_at: 'First seen at',
                ps_start_slug: 'Start slug',
                ps_start_page: 'Start page',
                ps_end_slug: 'End slug',
                ps_converted_at: 'Converted at',
                ps_completed_at: 'Completed at',
                ps_closed_at: 'Closed at',
                ps_last_seen_at: 'Last seen at',
                ps_converted: 'Has converted',
                ps_completed: 'Has completed',
                ps_closed: 'Has closed',
                ps_result_page: 'Result page',
                ps_calendly_booked: 'Calendly Booked',
                ps_cookies: 'Cookies accepted',
                utm_source: 'UTM Source',
                utm_medium: 'UTM Medium',
                utm_campaign: 'UTM Campaign',
                utm_term: 'UTM Term',
                utm_content: 'UTM Content',
            };

            pageData.fields.forEach((field) => {
                if (field.interactionType === 'answers' && field.additional.answers.length > 0) {
                    // question
                    trackingTitles[field.additional.answers[0].fieldName] = field.title;
                } else {
                    // form
                    field.additional.form.inputs.forEach((input) => {
                        trackingTitles[input.fieldName] = input.title;
                    });
                }
            });
        }

        return trackingTitles;
    };

    $: isHomePage = pageData.isHomePage || !!pageData.variantPageData?.pageId;
</script>

<div class="content">
    {#key dataToDisplay.pageId}
        {#each dataToDisplay.components as component, index}
            <DisplayComponent
                id={component._id}
                language={dataToDisplay.language}
                campaignId={dataToDisplay.campaignId}
                companyId={dataToDisplay.companyId}
                cookiesEnabled={dataToDisplay.cookiesEnabled}
                componentType={component.componentType}
                childComponents={component.components}
                content={component.content}
                theme={dataToDisplay.theme}
                nextPage={dataToDisplay.nextPage}
                currentPage={dataToDisplay.pageId}
                resultMapping={dataToDisplay.resultMapping}
                fields={dataToDisplay.fields}
                firstResultPage={dataToDisplay.firstResultPage}
                trackingProperties={dataToDisplay.trackingProperties}
                {index}
            />
        {/each}
    {/key}
</div>

{#if dataToDisplay.animation === 'confetti'}
    <svelte:component this={Confetti} />
{/if}

{#if dataToDisplay.bodyScript}
    {@html unescape(dataToDisplay.bodyScript)}
{/if}
